import React, { useState } from 'react';
import CircularGallery from '../components/CircularGallery';
import { useTranslation } from 'react-i18next';

// Gallery categories and images
const galleryCategories = [
  {
    id: 'spa-facilities',
    title: 'Spa Facilities',
    images: [
      {
        src: '/images/gallery/spa-entrance-archway.jpg',
        alt: 'Spa entrance with decorative archway',
        text: 'Grand Entrance',
      },
      {
        src: '/images/gallery/golden-lion-statues.jpg',
        alt: 'Golden lion statues at the spa entrance',
        text: 'Lion Guardians',
      },
      {
        src: '/images/gallery/treatment-room-equipment.jpg',
        alt: 'Professional treatment room with equipment',
        text: 'Treatment Room',
      },
      {
        src: '/images/gallery/facial-treatment-chamber.jpg',
        alt: 'Facial treatment chamber with specialized equipment',
        text: 'Facial Chamber',
      },
      {
        src: '/images/gallery/massage-therapy-suite.jpg',
        alt: 'Massage therapy suite',
        text: 'Massage Suite',
      },
      {
        src: '/images/gallery/lotus-garden-sanctuary.jpg',
        alt: 'Lotus garden sanctuary',
        text: 'Garden Sanctuary',
      },
      {
        src: '/images/gallery/purple-blossom-window.jpg',
        alt: 'Purple blossom window decoration',
        text: 'Blossom Window',
      },
      {
        src: '/images/gallery/lotus-meditation-lamp.jpg',
        alt: 'Lotus meditation lamp',
        text: 'Lotus Lamp',
      },
    ],
  },
  {
    id: 'treatments',
    title: 'Treatments',
    images: [
      {
        src: '/images/gallery/serene-treatment-bed.jpg',
        alt: 'Serene treatment bed prepared for session',
        text: 'Treatment Bed',
      },
      {
        src: '/images/gallery/meditation-room-scrolls.jpg',
        alt: 'Meditation room with traditional scrolls',
        text: 'Meditation Room',
      },
      {
        src: '/images/gallery/glowing-crystal-sphere.jpg',
        alt: 'Glowing crystal sphere for energy healing',
        text: 'Crystal Healing',
      },
      {
        src: '/images/gallery/herbal-sachets-welcome.jpg',
        alt: 'Herbal sachets welcome arrangement',
        text: 'Herbal Sachets',
      },
      {
        src: '/images/gallery/mountain-landscape-lamp.jpg',
        alt: 'Mountain landscape decorative lamp',
        text: 'Ambient Lighting',
      },
    ],
  },
  {
    id: 'products',
    title: 'Our Products',
    images: [
      {
        src: '/images/gallery/valmont-skincare-display.jpg',
        alt: 'Valmont skincare product display',
        text: 'Valmont Skincare',
      },
      {
        src: '/images/gallery/premium-essence-bottles.jpg',
        alt: 'Premium essence bottles collection',
        text: 'Premium Essences',
      },
      {
        src: '/images/gallery/la-mer-skincare-line.jpg',
        alt: 'La Mer skincare product line',
        text: 'La Mer Collection',
      },
      {
        src: '/images/gallery/whoospa-product-display.jpg',
        alt: 'WhoSpa signature product display',
        text: 'WhoSpa Signature',
      },
    ],
  },
];

function Gallery() {
  const { t } = useTranslation(['common']);
  const [selectedCategory, setSelectedCategory] = useState('spa-facilities');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  
  // Get current category
  const currentCategory = galleryCategories.find(cat => cat.id === selectedCategory) || galleryCategories[0];
  
  // Format items for circular gallery
  const circularGalleryItems = currentCategory.images.map(img => ({
    image: img.src,
    text: img.text
  }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8F6F2] via-[#E8F5F2] to-[#F9E9E9] font-sans">
      {/* Hero Section */}
      <section className="relative h-[40vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-[#232347]/60 z-10"></div>
        <div className="relative z-20 text-center px-4">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 font-serif">{t('common.galleryTitle')}</h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">{t('common.gallerySubtitle')}</p>
        </div>
        <div className="absolute inset-0 z-0">
          <img 
            src="/images/gallery/lotus-meditation-lamp.jpg"
            alt="Lotus meditation lamp ambiance" 
            className="w-full h-full object-cover"
          />
        </div>
      </section>
      
      <div className="container mx-auto py-16 px-4 md:px-6">
        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {galleryCategories.map((category) => (
            <button
              key={category.id}
              className={`px-6 py-2 rounded-full font-medium transition-colors ${selectedCategory === category.id ? 'bg-[#B2D8C5] text-white' : 'bg-white text-[#232347] hover:bg-[#F7CAC9] hover:text-white'}`}
              onClick={() => setSelectedCategory(category.id)}
            >
              {category.title}
            </button>
          ))}
        </div>
        
        {/* Interactive Gallery (Using CircularGallery for first section) */}
        <div className="mb-16">
          <h2 className="text-2xl md:text-3xl font-serif font-semibold text-[#232347] mb-6 text-center">Interactive Experience</h2>
          <p className="text-[#232347]/80 mb-8 text-center max-w-3xl mx-auto">Drag to explore our {currentCategory.title.toLowerCase()} in this interactive gallery</p>
          
          <div className="w-full h-[50vh] md:h-[60vh] rounded-xl overflow-hidden shadow-xl">
            <CircularGallery 
              items={circularGalleryItems}
              bend={3}
              textColor="#ffffff"
              borderRadius={0.05}
              font="bold 30px serif"
            />
          </div>
        </div>
        
        {/* Standard Gallery Grid */}
        <div className="mb-16">
          <h2 className="text-2xl md:text-3xl font-serif font-semibold text-[#232347] mb-6 text-center">{currentCategory.title}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {currentCategory.images.map((image, idx) => (
              <div 
                key={idx} 
                className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300 cursor-pointer"
                onClick={() => setSelectedImage(image.src)}
              >
                <div className="relative h-80 overflow-hidden">
                  <img 
                    src={image.src} 
                    alt={image.alt}
                    className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                  />
                </div>
                <div className="p-4 text-center">
                  <h3 className="text-lg font-semibold text-[#232347]">{image.text}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Testimonial Section */}
        <div className="bg-white/70 rounded-xl shadow-lg p-8 md:p-12 text-center mb-16">
          <h2 className="text-2xl md:text-3xl font-serif font-semibold text-[#232347] mb-6">Guest Experiences</h2>
          <p className="text-xl md:text-2xl italic text-[#232347]/80 mb-6 max-w-3xl mx-auto">
            "The atmosphere at Healing Lotus is truly transformative. From the moment I stepped in, I felt the stress melt away. The attention to detail in every corner of the spa creates an ambiance of pure serenity."
          </p>
          <p className="text-[#B2D8C5] font-medium">— Emily R., Valued Guest</p>
        </div>
        
        {/* Call to Action */}
        <div className="text-center">
          <h2 className="text-2xl md:text-3xl font-serif font-semibold text-[#232347] mb-4">Experience It Yourself</h2>
          <p className="text-[#232347]/80 mb-8 max-w-2xl mx-auto">Our images only capture a glimpse of the total experience. Visit us to discover the true essence of tranquility.</p>
          <a 
            href="/contact" 
            className="inline-block bg-[#B2D8C5] hover:bg-[#88bfa3] text-white px-8 py-3 rounded-md font-semibold text-lg transition-colors"
          >
            Book Your Visit
          </a>
        </div>
      </div>
      
      {/* Image Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-6xl w-full">
            <button 
              className="absolute top-4 right-4 bg-white/20 hover:bg-white/40 rounded-full p-2 text-white transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedImage(null);
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img 
              src={selectedImage} 
              alt="Enlarged view" 
              className="w-full h-auto max-h-[90vh] object-contain rounded-lg shadow-2xl"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default Gallery; 